import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Smartphone, X, Share } from "lucide-react";

const STORAGE_KEY = "coparent.installPrompt.v1";

// Detect if the app is already running standalone (installed as PWA)
function isStandalone() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone = window.navigator && "standalone" in window.navigator && window.navigator.standalone;
  return !!(mq || iosStandalone);
}

// Detect iOS (Safari) — no beforeinstallprompt event, we show manual instructions instead
function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator?.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua); // real Safari only
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    let dismissed = null;
    try { dismissed = localStorage.getItem(STORAGE_KEY); } catch (err) { console.warn("[InstallPrompt] localStorage read blocked:", err?.message || err); }
    if (dismissed) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS Safari never fires beforeinstallprompt — surface manual guide after 3s
    let t = null;
    if (isIOS()) {
      t = setTimeout(() => setVisible(true), 3000);
    }

    const onInstalled = () => {
      setVisible(false);
      try { localStorage.setItem(STORAGE_KEY, `installed:${Date.now()}`); } catch (err) { console.warn("[InstallPrompt] localStorage write blocked:", err?.message || err); }
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, `dismissed:${Date.now()}`); } catch (err) { console.warn("[InstallPrompt] localStorage write blocked:", err?.message || err); }
  };

  const install = async () => {
    if (isIOS()) { setShowIOSGuide(true); return; }
    if (!deferred) return;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        try { localStorage.setItem(STORAGE_KEY, `installed:${Date.now()}`); } catch (err) { console.warn("[InstallPrompt] localStorage write blocked:", err?.message || err); }
      } else {
        dismiss();
      }
    } catch (err) { console.error("[InstallPrompt] native prompt failed:", err?.message || err); }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  // iOS manual-steps overlay
  if (showIOSGuide) {
    return (
      <div className="fixed inset-0 z-50 bg-[#2E3A3F]/40 flex items-end sm:items-center justify-center p-4" data-testid="install-ios-guide">
        <div className="bg-white border border-[#D9E1E3] rounded-md w-full max-w-md p-5 space-y-3 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="font-heading text-lg font-medium inline-flex items-center gap-2"><Smartphone className="w-5 h-5 text-[#1F9FA4]" strokeWidth={1.5} /> Add to Home Screen</div>
            <button type="button" onClick={() => { setShowIOSGuide(false); dismiss(); }} className="text-[#6F828A] hover:text-[#2E3A3F]" data-testid="install-ios-close"><X className="w-4 h-4" /></button>
          </div>
          <ol className="text-sm text-[#2E3A3F] space-y-2 list-decimal pl-5">
            <li>Tap the <span className="inline-flex items-center gap-1 align-middle font-medium"><Share className="w-3.5 h-3.5" /> Share</span> icon at the bottom of Safari.</li>
            <li>Scroll and tap <span className="font-medium">&ldquo;Add to Home Screen&rdquo;</span>.</li>
            <li>Tap <span className="font-medium">Add</span> in the top-right to finish.</li>
          </ol>
          <p className="text-xs text-[#6F828A]">CoParent Console will open like a native app with its own icon and no browser bars.</p>
          <Button onClick={() => { setShowIOSGuide(false); dismiss(); }} className="w-full bg-[#1F9FA4] hover:bg-[#178085] rounded-sm" data-testid="install-ios-done">Got it</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-[#C8E0D4] bg-[#F1F8F4] rounded-md p-4 flex items-start gap-3" data-testid="install-prompt-banner">
      <div className="w-9 h-9 rounded-sm bg-white border border-[#C8E0D4] flex items-center justify-center flex-shrink-0">
        <Smartphone className="w-4 h-4 text-[#1F6A47]" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading text-base font-medium text-[#1F3A2A]">Install CoParent Console</div>
        <p className="text-sm text-[#2E3A3F] mt-0.5">Add it to your home screen for one-tap access, lock-screen notifications and a calmer, full-screen experience.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button onClick={install} size="sm" className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm" data-testid="install-prompt-install-button">
            <Download className="w-3.5 h-3.5 mr-1.5" /> {isIOS() ? "How to install" : "Install"}
          </Button>
          <Button onClick={dismiss} size="sm" variant="ghost" className="text-[#6F828A] hover:text-[#2E3A3F] rounded-sm" data-testid="install-prompt-dismiss-button">Not now</Button>
        </div>
      </div>
      <button type="button" onClick={dismiss} className="text-[#6F828A] hover:text-[#2E3A3F] flex-shrink-0" aria-label="Dismiss" data-testid="install-prompt-close">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
